import useSWR from 'swr';
import { NewsItem } from '@/lib/api';

interface NewsParams {
  page?: number;        // Номер страницы (1, 2, 3...)
  limit?: number;       // Размер страницы
  category?: string;
  tag?: string;
  search?: string;
  ordering?: string;
}

interface NewsResponse {
  results: NewsItem[];
  count: number;
  next?: string;
  previous?: string;
}

// Генерация ключа для SWR с параметрами (PHP API: page + limit)
function getNewsKey(params: NewsParams): string {
  const query = new URLSearchParams();
  // page и limit для PHP API
  if (params.page !== undefined) query.set('page', String(params.page));
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.category) query.set('category', params.category);
  if (params.tag) query.set('tag', params.tag);
  if (params.search) query.set('search', params.search);
  if (params.ordering) query.set('ordering', params.ordering);

  const queryString = query.toString();
  return queryString ? `/api/news/?${queryString}` : '/api/news/';
}

// Хук для списка новостей с пагинацией и фильтрами
export function useNews(params: NewsParams = {}) {
  const key = getNewsKey(params);

  const { data, error, isLoading, isValidating, mutate } = useSWR<NewsResponse | NewsItem[]>(
    key,
    {
      // Новости обновляются чаще — раз в 60 секунд
      refreshInterval: 60000,
      revalidateIfStale: true,
      // Принудительная ревалидация при смене ключа (страницы)
      revalidateOnMount: true,
      // Не сохранять предыдущие данные при пагинации для более явного UX
      keepPreviousData: false,
    }
  );

  // Нормализация ответа (DRF возвращает объект с results и count при пагинации)
  const normalized = (() => {
    if (!data) return { results: [], count: 0 };
    // Массив — старый формат без пагинации (fallback)
    if (Array.isArray(data)) return { results: data, count: data.length };
    // Объект с пагинацией — стандартный ответ DRF
    const results = Array.isArray(data.results) ? data.results : [];
    const count = typeof data.count === 'number' ? data.count : results.length;
    return { results, count };
  })();

  return {
    news: normalized.results,
    totalCount: normalized.count,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Хук для одной новости по slug
export function useNewsItem(slug: string | null) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<NewsItem>(
    slug ? `/api/news/${encodeURIComponent(String(slug || '').trim())}/` : null,
    {
      revalidateIfStale: true,
    }
  );

  return {
    news: data ?? null,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}
